import React from 'react';
import { Select, MenuItem, ListSubheader, Divider } from '@mui/material';
import styled from 'styled-components';

const StyledListSubheader = styled(ListSubheader)`
        color: 'black',
        position: 'static',
        background-color: 'white',
        line-height: 2,
`;

interface PhoneNumber {
    billingCode: string;
    creditGroupName: string | null;
    creditPlanIdOrGroupId: string;
    creditPlanName: string;
    creditType: string;
    minLimitType: string;
    minsOwed: number;
    minsPaid: number;
    minsPrepaid: number;
    number: string;
    numberTitle: string | null;
    numberTypeId: string;
    ownedByDesc: string;
    ownedByEmail: string;
    ownedByName: string;
    ownedByType: string;
    status: string;
    usedByDesc: string;
    usedByEmail: string;
    usedByName: string;
}

interface CallLogsDropdownProps {
    numbers: PhoneNumber[];
    user: { email: string };
    onNumberChange: (e: React.ChangeEvent<{ value: unknown }>) => void;
}
const CallLogsDropdown: React.FC<CallLogsDropdownProps> = ({ numbers, user, onNumberChange }) => {
    const categorizeNumbers = () => {
        const categorized = {
            ownedBy: [] as PhoneNumber[],
            usedBy: [] as PhoneNumber[],
            managedBy: [] as PhoneNumber[],
        };
        const addedCreditGroupNames = new Set<string>();

        numbers.forEach((num) => {
            const isOwnedBy = num.ownedByEmail === user.email;
            const isUsedBy = num.usedByEmail === user.email;

            if (num.creditGroupName && addedCreditGroupNames.has(num.creditGroupName)) return;

            if (isOwnedBy) {
                categorized.ownedBy.push(num);
            } else if (isUsedBy) {
                categorized.usedBy.push(num);
            } else {
                categorized.managedBy.push(num);
            }

            if (num.creditGroupName) {
                addedCreditGroupNames.add(num.creditGroupName);
            }
        });

        const numberTypeOrder = ['twilio', 'external', 'vri', 'extn'];

        const sortByCreditGroupNameAndTypeId = (a: PhoneNumber, b: PhoneNumber) => {
            if (a.creditGroupName && !b.creditGroupName) return -1;
            if (!a.creditGroupName && b.creditGroupName) return 1;

            const typeIdAIndex = numberTypeOrder.indexOf(a.numberTypeId);
            const typeIdBIndex = numberTypeOrder.indexOf(b.numberTypeId);

            if (typeIdAIndex !== typeIdBIndex) {
                return typeIdAIndex - typeIdBIndex;
            }

            const creditGroupNameA = a.creditGroupName || '';
            const creditGroupNameB = b.creditGroupName || '';
            return creditGroupNameA.localeCompare(creditGroupNameB);
        };

        categorized.ownedBy.sort(sortByCreditGroupNameAndTypeId);
        categorized.usedBy.sort(sortByCreditGroupNameAndTypeId);
        categorized.managedBy.sort(sortByCreditGroupNameAndTypeId);

        return categorized;
    };

    const { ownedBy, usedBy, managedBy } = categorizeNumbers();

    const renderMenuItems = (items: PhoneNumber[], label: string, showDivider: boolean) =>
        items.length > 0 && [
            <StyledListSubheader key={`${label}-header`}>
                <b>{label}</b>
            </StyledListSubheader>,
            ...items.map((num) => (
                <MenuItem key={num.number} value={JSON.stringify(num)}>
                    {num.creditGroupName || num.number}
                </MenuItem>
            )),
            showDivider && <Divider key={`${label}-divider`} />,
        ];

    return (
        <Select
            labelId="call-logs-dropdown"
            id="call-logs-select"
            onChange={onNumberChange as any}
            //label="Select"
        >
            {renderMenuItems(ownedBy, 'Accounts I own:', usedBy.length > 0 || managedBy.length > 0)}
            {renderMenuItems(usedBy, 'Accounts I use:', managedBy.length > 0)}
            {renderMenuItems(managedBy, 'Accounts I manage:', false)}
        </Select>
    );
};

export default CallLogsDropdown;
